import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Calendar, AlertCircle, Users, MapPin, Clock, Sparkles } from 'lucide-react';
import { Alert, AlertDescription } from './ui/alert';
import { Badge } from './ui/badge';
import { Skeleton } from './ui/skeleton';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { logger } from '../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function EventRSVPWizardModal({ 
  isOpen, 
  onClose, 
  lead, 
  onSuccess 
}) {
  const [events, setEvents] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingEvents, setLoadingEvents] = useState(true);
  const [error, setError] = useState('');

  // Fetch upcoming events when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchEvents();
      setSelectedEventId('');
      setNotes('');
      setError('');
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchEvents = async () => {
    setLoadingEvents(true);
    try {
      // Fetch upcoming/active events
      const response = await fetch(`${API_URL}/api/events?upcoming=true`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        // Filter to only upcoming events (start_datetime in future)
        const now = new Date().toISOString();
        const upcomingEvents = data.filter(e => 
          e.start_datetime > now && e.status !== 'cancelled'
        );
        setEvents(upcomingEvents);
      } else {
        logger.error('Failed to fetch events');
        setEvents([]);
      }
    } catch (err) {
      logger.error('Error fetching events:', err);
      setEvents([]);
    } finally {
      setLoadingEvents(false);
    }
  };

  const formatEventDate = (dateStr) => {
    try {
      return format(new Date(dateStr), 'EEE, MMM d, yyyy h:mm a');
    } catch {
      return dateStr;
    }
  };

  const handleSubmit = async () => {
    // Validation
    if (!selectedEventId) {
      setError('Please select an event');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // RSVP lead to event and update lead stage
      const response = await fetch(`${API_URL}/api/leads/${lead.lead_id}/rsvp-event`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          event_id: selectedEventId,
          notes: notes || undefined
        })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success('Lead registered for event!');
        onSuccess && onSuccess(data);
        onClose();
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to register for event');
      }
    } catch (err) {
      logger.error('Error registering for event:', err);
      setError('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const selectedEvent = events.find(e => e.event_id === selectedEventId);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="event-rsvp-wizard-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Sparkles className="h-5 w-5 text-amber-500" />
            RSVP for Office Event
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5 py-4">
          {/* Lead Info */}
          <div className="bg-slate-50 p-3 rounded-lg">
            <p className="text-sm text-slate-500">Registering:</p>
            <p className="font-medium text-slate-900">
              {lead?.first_name} {lead?.last_name}
            </p>
            {lead?.email && <p className="text-sm text-slate-500">{lead.email}</p>}
          </div>

          {error && (
            <Alert variant="destructive" className="bg-red-50 border-red-200">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Event Selection */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">
              Select Event <span className="text-red-500">*</span>
            </Label>
            
            {loadingEvents ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} className="h-20 w-full rounded-xl" />
                ))}
              </div>
            ) : events.length === 0 ? (
              <div className="text-center py-8 bg-slate-50 rounded-xl">
                <Calendar className="h-10 w-10 text-slate-300 mx-auto mb-2" />
                <p className="text-slate-500">No upcoming events available</p>
                <p className="text-xs text-slate-400 mt-1">Create an event first to register leads</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-[300px] overflow-y-auto pr-1">
                {events.map((event) => {
                  const isSelected = selectedEventId === event.event_id;
                  return (
                    <button
                      key={event.event_id}
                      type="button"
                      onClick={() => setSelectedEventId(event.event_id)}
                      className={`w-full flex items-start gap-3 p-4 rounded-xl border-2 transition-all text-left ${
                        isSelected 
                          ? 'border-amber-500 bg-amber-50' 
                          : 'border-slate-200 hover:border-slate-300 bg-white'
                      }`}
                      data-testid={`event-option-${event.event_id}`}
                    >
                      <div className={`p-2 rounded-lg flex-shrink-0 ${isSelected ? 'bg-amber-100 text-amber-600' : 'bg-slate-100 text-slate-500'}`}>
                        <Calendar className="h-5 w-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`font-medium truncate ${isSelected ? 'text-amber-900' : 'text-slate-900'}`}>
                          {event.name}
                        </p>
                        <div className="flex items-center gap-2 mt-1 text-xs text-slate-500">
                          <Clock className="h-3 w-3" />
                          <span>{formatEventDate(event.start_datetime)}</span>
                        </div>
                        {event.location && (
                          <div className="flex items-center gap-2 mt-0.5 text-xs text-slate-500">
                            <MapPin className="h-3 w-3" />
                            <span className="truncate">{event.location}</span>
                          </div>
                        )}
                        {event.capacity && (
                          <div className="flex items-center gap-2 mt-0.5">
                            <Users className="h-3 w-3 text-slate-400" />
                            <span className="text-xs text-slate-500">Capacity: {event.capacity}</span>
                          </div>
                        )}
                        {event.category && (
                          <Badge variant="outline" className="mt-2 text-[10px]">
                            {event.category}
                          </Badge>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Selected Event Summary */}
          {selectedEvent && (
            <div className="bg-amber-50 border border-amber-200 p-3 rounded-lg">
              <p className="text-sm font-medium text-amber-800">Selected:</p>
              <p className="text-amber-900">{selectedEvent.name}</p>
              <p className="text-xs text-amber-700 mt-1">{formatEventDate(selectedEvent.start_datetime)}</p>
            </div>
          )}

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="rsvp-notes" className="text-sm font-medium">
              Notes (optional)
            </Label>
            <Textarea
              id="rsvp-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add any notes about this registration..."
              className="input-goldsands min-h-[80px]"
              data-testid="rsvp-notes"
            />
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            disabled={loading || !selectedEventId}
            className="btn-accent"
            data-testid="rsvp-submit"
          >
            {loading ? 'Registering...' : 'Register for Event'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
